import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const LIBREOFFICE_TUTORIAL_PROJECT_ID = 'b3a6fbf7-0eab-4110-a5f2-57597bde38ae';

const LIBREOFFICE_TUTORIAL_TASKS: TutorialTask[] = [
  {
    id: 'libreoffice-project-tour',
    name: 'Explore the labeling workspace',
    type: 'success',
  },
  {
    id: 'libreoffice-obvious-success',
    name: 'Obvious success',
    type: 'success',
    runId: 'c38cb81e-24fb-40ad-850e-27bffa6fcf37',
  },
  {
    id: 'libreoffice-obvious-failure',
    name: 'Obvious failure',
    type: 'failure',
    runId: 'c97222b0-b220-4946-a2f8-4799246b872b',
  },
  {
    id: 'libreoffice-deceptive-success-1',
    name: 'Success that looks like fail',
    type: 'success',
    runId: 'fe183471-2021-40e2-9073-27fbf5f1a4a3',
  },
  {
    id: 'libreoffice-deceptive-success-2',
    name: 'Another success that looks like fail',
    type: 'success',
    runId: '3e0f5dcd-5ed9-41f1-85ea-e953e8e2c21a',
  },
  {
    id: 'libreoffice-deceptive-failure',
    name: 'Fail that looks like success',
    type: 'failure',
    runId: '59121f4c-e40e-4552-ae33-ccf5fc6aaaf6',
  },
  {
    id: 'libreoffice-ambiguous-task',
    name: 'Ambiguous problem',
    type: 'bad_task',
    runId: 'f40a5112-dbe1-4c40-8065-44194ce95141',
  },
];

const LIBREOFFICE_TUTORIAL_STEPS: TutorialStep[] = [
  // ============================================================================
  // SHARED PROJECT TOUR FRAMES (frames 1-13)
  // Keep these identical across all tutorials
  // ============================================================================
  {
    id: 'libreoffice-project-welcome',
    title: 'Welcome to LibreOffice Labeling',
    content:
      'This tutorial gives you a quick tour of the labeling workspace. You can explore every part of the UI without affecting real data.\n\nOnce you claim a labeling project, you can click on the project to be brought to this page.\n\nIf you would like to exit the tutorial, just click "Exit Tutorial" on the top bar. You can always restart the tutorial by clicking "Show Tutorial."',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },
  {
    id: 'libreoffice-problem-list',
    title: 'Assigned Problems',
    content:
      'Each card in this list represents a problem. The badge tracks how many runs you have labeled. Feel free to click through the list during the tutorial.',
    targetSelector: '.problem-list',
    action: 'explore',
    position: 'right',
    allowInteraction: true,
    scope: 'project',
  },
  {
    id: 'libreoffice-solution-upload',
    title: 'Solution Uploads',
    content:
      'Projects consist of two steps: first, you must upload the solution for the problem, and next, you should label all of the runs for that problem. When uploading your solution, make sure that your uploaded filename matches exactly the filename of the solution requested in the prompt.',
    targetSelector: '.solution-upload-section',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'libreoffice-bad-task-button',
    title: 'Mark Ambiguous Problems',
    content:
      'If the prompt is ambiguous or has multiple valid outcomes, mark it as a bad task here. Provide a concise reason so reviewers understand the issue. After marking as ambiguous, all runs will automatically be labeled and you will not have to upload a solution.',
    targetSelector: '.bad-task-btn',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'solution-upload-requirement',
    title: 'Solution Required',
    content:
      'Important: You must upload your solution before you can select and label any runs. This ensures you have solved the problem yourself before evaluating the AI\'s attempts. Notice that the runs below are currently grayed out.',
    targetSelector: '.run-list',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'solution-upload-select-run',
    title: 'Runs to Review',
    content:
      'Every run shows a model attempt. Now you can select one to open the viewer so you can study exactly how the agent approached the task. Click on any run below to proceed.',
    targetSelector: '.run-list',
    action: 'click',
    position: 'left',
    allowInteraction: true,
    scope: 'project',
    nextButtonLabel: 'Select a run to proceed',
  },
  {
    id: 'libreoffice-viewer-overview',
    title: 'Review Each Attempt',
    content:
      'The video displays the AI solving the task. The AI has a computer environment consisting of many applications like Audacity, LibreOffice, Inkscape, Gimp, and Shotcut, and can use any of these applications to solve the task. The AI can also use programming and bash scripting. Anything is fair game as long as the AI solves the task as requested!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'libreoffice-viewer-timeline',
    title: 'Timeline Controls',
    content:
      'Use the timeline to jump between frames. Speed controls and keyboard shortcuts help you inspect long attempts quickly without skipping critical moments.',
    targetSelector: '.timeline-container',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'libreoffice-viewer-command-panel',
    title: 'Command History',
    content:
      'The agent can launch shell commands, scripts, or custom tools while it works. Every command it runs appears here so you can verify exactly what code or terminal actions were executed.',
    targetSelector: '.tool-output-section:first-child',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'libreoffice-viewer-thoughts',
    title: 'Agent Notes',
    content:
      'Read the thought bubble to see why the agent acted. When judging ambiguity, these notes often reveal whether the prompt had multiple valid interpretations.',
    targetSelector: '.thought-process',
    action: 'next',
    position: 'right',
    scope: 'viewer',
  },
  {
    id: 'libreoffice-viewer-inspect',
    title: 'Inspect the Attempt',
    content:
      'Watch the video carefully to see every step the AI took. When you need the final output, right-click the video to download the files it produced and compare them with your ground-truth solution.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'libreoffice-viewer-evaluate',
    title: 'Label the Run',
    content:
      'Use this panel to decide whether the run succeeded or failed. If you realize that a problem is ambiguous or has multiple correct solutions, return to the problem workspace, mark it as a bad task, and then move on to the next problem.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    maxWidth: 360,
    scope: 'viewer',
    offsetY: -50,
  },
  {
    id: 'libreoffice-viewer-return',
    title: 'Return to the Problem Workspace',
    content:
      'Click "Back to Problem" when you finish reviewing runs. That returns you to the overview so you can upload a solution or mark the problem ambiguous.',
    targetSelector: '.back-to-problem-btn',
    action: 'click',
    position: 'right',
    scope: 'viewer',
    allowInteraction: true,
  },
  {
    id: 'libreoffice-project-recap',
    title: 'Wrap Up the Problem',
    content:
      'From this workspace you can revisit the prompt, upload the required solution file, and finish labeling every run. Ambiguous problems can be waived entirely.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'libreoffice-example-transition',
    title: 'Run Labeling Examples',
    content:
      'Now let us review real AI runs and decide whether each is a success or a failure. We will walk through several examples together.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },

  // ============================================================================
  // LIBREOFFICE-SPECIFIC EXAMPLE STEPS
  // ============================================================================

  // Example 1: Obvious Success
  {
    id: 'libreoffice-obvious-success-intro',
    title: 'Obvious Success',
    content:
      'In this run, the AI clearly center-aligns the poem, deletes the footer, and saves its changes. Watch the video and you will see the AI successfully completes all the required steps.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
    offsetY: 600,
  },
  {
    id: 'libreoffice-obvious-success-conclusion',
    title: 'Clear Success',
    content:
      'The AI completed the task exactly as requested. This is an obvious success. Mark it as a success and move on.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 2: Obvious Failure
  {
    id: 'libreoffice-obvious-failure-intro',
    title: 'Obvious Failure',
    content:
      'In this run, the AI forgot to add a space after the commas when replacing the em dashes. Watch the video carefully and you will see this clear mistake.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
    offsetY: 600,
  },
  {
    id: 'libreoffice-obvious-failure-conclusion',
    title: 'Mark as Failure',
    content:
      'Because the AI did not properly format the replacements (missing spaces after commas), this run is an obvious failure. Mark it as a failure and move on.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 3: Success That Looks Like Fail (Terminal/Code)
  {
    id: 'libreoffice-deceptive-success-1-intro',
    title: 'Looks Like Failure, But Check Carefully',
    content:
      'At first glance, this run looks like it must be a fail. After all, the AI did not even open LibreOffice! But remember: the AI also has access to the terminal and can execute arbitrary code to directly modify files.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'libreoffice-deceptive-success-1-details',
    title: 'The AI Uses Python Code',
    content:
      'If we look at the bottom of the screen, we see that the AI is writing and executing Python code to modify the document directly.',
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 4,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'libreoffice-deceptive-success-1-conclusion',
    title: 'Output File is the Source of Truth',
    content:
      'If we download the output file (right-click the video), we see that the AI has perfectly completed this task. The output file is the source of truth when determining if the AI was successful. You should always download and inspect it to double-check your label if you are unsure! This run should be labeled a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -100,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 4: Another Success That Looks Like Fail (Code + LibreOffice)
  {
    id: 'libreoffice-deceptive-success-2-intro',
    title: 'Code AND LibreOffice Together',
    content:
      'Sometimes the AI will open LibreOffice AND write code to solve a problem. In these cases, just relying on the video can be downright misleading!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
    offsetY: 600,
  },
  {
    id: 'libreoffice-deceptive-success-2-details',
    title: 'Video Shows Nothing Changed',
    content:
      'If we just rely on the video, it looks like the AI has not done anything at all. But in reality, the AI wrote some code that deleted all the roman numeral subscripts.',
    targetSelector: '.screenshot-wrapper',
    action: 'seek-frame',
    seekFrame: 27,
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'libreoffice-deceptive-success-2-conclusion',
    title: 'Always Verify with Output File',
    content:
      'If we download the output file, we see that the AI actually completed the task correctly. Never trust the video alone—always download and verify the output file! This run should be labeled a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 5: Fail That Looks Like Success
  {
    id: 'libreoffice-deceptive-failure-intro',
    title: 'Looks Like Success, But Verify Everything',
    content:
      'Conversely, you will also see videos that look like successes at first glance, but are actually failures. If we scroll through the video, it looks like the AI has completed the task perfectly.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
    offsetY: 600,
  },
  {
    id: 'libreoffice-deceptive-failure-details',
    title: 'Check All Requirements Carefully',
    content:
      'Each of the 3 original documents is on its own page, in the right order, combined in a single doc. However, the task description also says each document\'s formatting must "remain unchanged." If we download the 3 input files, we notice that preamble.docx was written in Garamond font size 14, and bof.docx was written in Calibri font size 13.',
    targetSelector: '.screenshot-wrapper',
    action: 'seek-frame',
    seekFrame: 31,
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'libreoffice-deceptive-failure-conclusion',
    title: 'Formatting Was Not Preserved',
    content:
      'In the AI\'s output, both of these are size 12 Times New Roman! The formatting was not preserved as required. You cannot just blindly trust the video—always inspect the input file(s) and output file(s) to confirm your label is correct. This run is actually a failure.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -75,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 6: Ambiguous Problem
  {
    id: 'libreoffice-ambiguous-task-intro',
    title: 'Ambiguous Problem',
    content:
      'Here is an example of a problem that is clearly ambiguous. The AI can make up whichever two themes it wants and it would technically be correct according to the task description.',
    targetSelector: '.task-description',
    action: 'next',
    position: 'bottom',
    scope: 'viewer',
    offsetY: 40,
  },
  {
    id: 'libreoffice-ambiguous-task-details',
    title: 'Multiple Valid Interpretations',
    content:
      'Additionally, it does not specify where in the list the 2 new themes should be added—the bottom? The second and third positions? Somewhere else? Even more subtly, it is unclear how the surrounding spacing should be handled when deleting the first paragraph. All of these different interpretations lead to different, equally correct answers.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
    offsetY: 600,
  },
  {
    id: 'libreoffice-ambiguous-task-conclusion',
    title: 'Mark Ambiguous Problems',
    content:
      'In cases like these, you should go back to the problem page and mark the task as ambiguous. This will save you time by marking the entire problem, its solution, and its run labels as fully complete.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
  },

  // Final Step
  {
    id: 'tutorial-complete',
    title: 'Nice Work!',
    content:
      'You have finished the LibreOffice tutorial. You now know how to tour the workspace, inspect outputs, download and compare documents, and identify when the AI uses code instead of the UI. Remember: the output file is the source of truth—always download and verify it, especially when the AI writes code. Also inspect input files to verify all requirements are met. Good luck on real projects!',
    action: 'next',
    position: 'bottom',
    scope: 'viewer',
  },
];

export const libreofficeTutorialConfig: TutorialConfig = {
  software: 'libreoffice',
  displayName: 'LibreOffice Labeling Tutorial',
  totalTasks: LIBREOFFICE_TUTORIAL_TASKS.length,
  tasks: LIBREOFFICE_TUTORIAL_TASKS,
  steps: LIBREOFFICE_TUTORIAL_STEPS,
  completionKey: 'labelPlatform_libreoffice_TutorialCompleted',
  dataSource: 'api',
  projectId: LIBREOFFICE_TUTORIAL_PROJECT_ID,
};
